import React from 'react'
import { createOperationDescriptor, getRequest, GraphQLTaggedNode, OperationType } from 'relay-runtime'
import { useRelayEnvironment } from 'react-relay'
import { fetchQuery } from 'api/graphql'
import { trackError } from 'services/analytics'

export type RelayCacheConfig = Parameters<typeof fetchQuery>[2] & { fetchKey?: string | number }
export type UseBackgroundQueryResult<T extends OperationType> = {
  data: T['response'] | null
  error: Error | null
}

/**
 * Performs a background query to retrieve results without suspending; similar to useLazyLoadQuery.
 * The query is only executed on the client (i.e. not during SSR).
 *
 * @param query The query to execute.
 * @param variables Query input variables.
 * @param config Relay configuration.
 * @returns An object containing the response value, or undefined if it's not yet loaded.
 */
export function useBackgroundQuery<T extends OperationType>(
  query: GraphQLTaggedNode,
  variables: T['variables'] = {},
  config: RelayCacheConfig = {},
): UseBackgroundQueryResult<T> {
  const effectKey = JSON.stringify(variables)

  const relayEnv = useRelayEnvironment()
  const [data, setData] = React.useState<T['response'] | null>(null)
  const [error, setError] = React.useState<Error | null>(null)
  const pollBlockStateRef = React.useRef<boolean>(false)
  const queryConcurrencyHandleRef = React.useRef<number>(0)

  const performFetch = async (additionalConfig: RelayCacheConfig = {}) => {
    pollBlockStateRef.current = true
    queryConcurrencyHandleRef.current += 1

    const currentConcurrencyHandle = queryConcurrencyHandleRef.current

    try {
      const result = await fetchQuery<T>(query, variables, { ...config, ...additionalConfig })

      if (currentConcurrencyHandle === queryConcurrencyHandleRef.current) {
        setData(result)
        pollBlockStateRef.current = false
      }
    } catch (err) {
      trackError(err as Error, 'high', {
        query,
        variables,
        config,
        additionalConfig,
        currentConcurrencyHandle,
        hook: 'useBackgroundQuery',
      })
      if (currentConcurrencyHandle === queryConcurrencyHandleRef.current) {
        setError(err as Error)
      }
    }
  }

  // perform the initial fetch for data, and retain that data
  // until this finishes, the data is considered loading
  React.useEffect(() => {
    // retain the query for optimization, i.e. so that other parts of the app can use its stored results
    const queryRequest = getRequest(query)
    const queryDescriptor = createOperationDescriptor(queryRequest, variables)
    const queryReference = relayEnv.retain(queryDescriptor)

    performFetch()

    // note that, despite the name, this will merely enable the query for "GC", but not actually dispose it right away
    return () => queryReference.dispose()
  }, [effectKey, config.fetchKey])

  // perform "offline" polling to periodically pull local data out of the relay store
  // TODO: this should ideally use a store event subscription to cause re-renders, instead of polling
  React.useEffect(() => {
    const pollIntervalHandle = window.setInterval(() => {
      if (!pollBlockStateRef.current) {
        // due to how this hook works, data is already in the store when the fetch from this call has a chance to run
        // thus, no network requests are made from this polling call
        performFetch({ fetchPolicy: 'store-or-network' })
      }
    }, 1000)

    return () => window.clearInterval(pollIntervalHandle)
  }, [effectKey])

  return { data, error }
}
